<template>
  <div>
    <div class="air__utils__heading">
      <h5>Exam Remark Entry</h5>
    </div>
    <div class="card" v-if="pagepermission.viewable">
    <div class="card">
      <div class="card-body">
        <div class="row mb-4">
          <div class="col-md-6 col-12 mb-3 mb-md-0">
            <label class="form-label">Select Exam</label>
            <a-select
              show-search
              placeholder="Select an exam"
              style="width: 100%"
              v-model="selectedExamName"
              :loading="loadingExams"
              @change="handleExamNameChange"
              option-filter-prop="children"
            >
              <a-select-option v-for="examName in uniqueExamNames" :key="examName" :value="examName">
                {{ examName }}
              </a-select-option>
            </a-select>
          </div>
          <div class="col-md-6 col-12">
            <label class="form-label">Select Class - Section</label>
            <a-select
              show-search
              placeholder="Select class and section"
              style="width: 100%"
              v-model="selectedExamId"
              :disabled="!selectedExamName"
              @change="handleExamChange"
              option-filter-prop="children"
            >
              <a-select-option v-for="exam in filteredClassSections" :key="exam.class_section_exam_id" :value="exam.class_section_exam_id">
                {{ exam.class_name }} - {{ exam.section_name }}
              </a-select-option>
            </a-select>
          </div>
        </div>

        <div v-if="selectedExamId && fullExamData">
          <remark-entry-table
            :students="students"
            :loading="loadingData"
            :exam-data="fullExamData"
            @refresh="handleExamChange"
          />
        </div>
        <div v-else-if="!loadingExams" class="text-center py-5">
          <a-empty description="Please select an exam to start entering remarks" />
        </div>
      </div>
    </div>
    </div>
    <div v-else>
      <p class="error">
        You don't have permission to use this feature.
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import RemarkEntryTable from '@/components/exam/RemarkEntry'

export default {
  name: 'ExamRemarkEntry',
  components: {
    RemarkEntryTable,
  },
  beforeMount() {
    this.$roleutility.checkpermission('exam', 22).then(data => {
      this.pagepermission = data.pagePermission
    })
  },
  data() {
    return {
      loadingExams: false,
      loadingData: false,
      assignedExams: [],
      selectedExamId: undefined,
      selectedExamName: undefined,
      fullExamData: null,
      students: [],
      pagepermission: {
        insertable: false,
        editable: false,
        viewable: false,
        deletable: false,
        approvable: false,
      },
    }
  },
  computed: {
    ...mapGetters(['user']),
    uniqueExamNames() {
      const names = this.assignedExams.map((e) => e.exam_name).filter(Boolean)
      return [...new Set(names)].sort()
    },
    filteredClassSections() {
      if (!this.selectedExamName) return []
      return this.assignedExams.filter((e) => e.exam_name === this.selectedExamName)
    },
  },
  watch: {
    user: {
      immediate: true,
      handler(val) {
        if (val && val.id && this.assignedExams.length === 0) {
          this.getAssignedExams()
        }
      },
    },
  },
  mounted() {
    this.getAssignedExams()
  },
  methods: {
    getAssignedExams() {
      if (!this.user || !this.user.id) return

      this.loadingExams = true
      this.$http
        .get('/api/staff/marks/assigned-exams', {
          params: {
            staff_id: this.user.id,
            type: 'remark',
          },
        })
        .then(({ data }) => {
          this.assignedExams = data.data || []
        })
        .catch((error) => {
          this.$neoNotify.error(error)
        })
        .finally(() => {
          this.loadingExams = false
        })
    },
    handleExamNameChange() {
      this.selectedExamId = undefined
      this.fullExamData = null
      this.students = []
    },
    handleExamChange(val) {
      const examId = val || this.selectedExamId
      if (!examId) {
        this.fullExamData = null
        this.students = []
        return
      }

      this.fetchData(examId)
    },
    fetchData(examId) {
      this.loadingData = true

      this.$http.get('/api/staff/marks/exam-students-remarks', {
        params: { class_section_exam_id: examId },
      })
        .then((res) => {
          const apiData = res.data.data || [] // This is the array of students

          if (apiData.length === 0) {
            this.students = []
            this.fullExamData = null
            return
          }

          // Aggregate unique subjects across all students
          const subjectMap = new Map()
          apiData.forEach(student => {
            if (student.marks && Array.isArray(student.marks)) {
              student.marks.forEach(m => {
                if (!subjectMap.has(m.subject_abbr)) {
                  subjectMap.set(m.subject_abbr, {
                    id: m.subject_abbr,
                    subject: { abbreviation: m.subject_abbr },
                    has_theory: false,
                    has_practical: false,
                    scholastic: m.scholastic,
                    co_scholastic: m.co_scholastic,
                  })
                }
              })
            }
          })
          const syntheticSubjects = Array.from(subjectMap.values())

          const mappedStudents = apiData.map(student => {
            return {
              ...student,
              // Remarks and basic info are now top-level in the new JSON
              result: student.status || '-',
              // The table component will look for 'marks' array or we can flatten it here
            }
          })

          this.fullExamData = {
            class_section_exam_subject: syntheticSubjects,
            id: examId,
          }
          this.students = mappedStudents
        })
        .catch((error) => {
          this.$neoNotify.error(error)
        })
        .finally(() => {
          this.loadingData = false
        })
    },
  },
}
</script>
